<script setup lang="ts">
import { locale_float } from '~/types/shared'
const { t } = useI18n()

const { userId } = defineProps<{
	userId: string
}>()

const page = ref(1)
const per_page = ref(10)
const topupOrders = ref<any>(null)

const topupOrdersColumns = computed(() => [
	{ label: t('data'), key: 'created_at', sortable: true },
	{ label: t('type'), key: 'slug', sortable: false },
	{ label: t('status_label'), key: 'status', sortable: false },
	{ label: t('amount'), key: 'deposit_amount', sortable: true },
	{ label: t('card'), key: 'cs_id', sortable: false },
	{ label: t('fee'), key: 'fees_processing_result.project_final_profit_usdt_total', sortable: false },
])

const cs_id_map = computed(() => {
	return topupOrders.value?.cs || {}
})

const fetchTopupOrders = async () => {
	const api = useApi()
	if (!userId) return

	const requestBody: any = {
		filter: { user_id: { '$oid': userId } },
		skip: (page.value - 1) * per_page.value,
		limit: per_page.value,
		sort: { created_at: -1 },
	}
	topupOrders.value = await api.post('/api/cs_topup_orders/find', requestBody)
	return topupOrders.value
}

onMounted(async () => {
	if (!topupOrders.value) {
		await fetchTopupOrders()
	}
})

watch([page, per_page], () => {
	fetchTopupOrders()
})

</script>

<template>
	<div>
		<ResponsiveTable
			:rows="topupOrders?.items"
			:columns="topupOrdersColumns"
		>
			<template #created_at-data="{ row }">
				<DateColumn :row="row" />
			</template>
			<template #slug-data="{ row }">
				{{  }}
				<SlugTypeColumn :row="row"  :slug-type-id="row.ax_data?.templateId ?? row.hp_data?.productId"/>
			</template>
			<template #status-data="{ row }">
				<CardTopupStatusColumn :row="row" @update="fetchTopupOrders" />
			</template>
			<template #deposit_amount-data="{ row }">
				<div class="space-y-2">
					<div class="flex items-center space-x-2">
						<UIcon name="i-cryptocurrency-usdt" class="w-5 h-5 text-green-600"/>
						<div class="text-zinc-800 dark:text-zinc-300 font-mono text-lg">{{ row.deposit_amount?.toLocaleString('ru-RU', {maximumFractionDigits :2}) || 0 }}</div>
					</div>
					<div v-if="row?.fees_processing_result?.user_card_topup_final_amount_usdt" class="text-sm text-zinc-500">
						{{ locale_float(row?.fees_processing_result?.user_card_topup_final_amount_usdt) }} USD
					</div>
				</div>
			</template>
			<template #cs_id-data="{ row }">
					<CardTopupCardColumn :row="row" :cs_id_map="cs_id_map" />
			</template>
			<template #fees_processing_result.project_final_profit_usdt_total-data="{ row }">
				<div class="space-y-1 text-zinc-800 dark:text-zinc-300" v-if="row.is_fees_processed">
					<p class="text-green-600">{{ $t('project-profit') }}: {{ locale_float(row.fees_processing_result?.project_final_profit_usdt_total) }} USDT</p>

					<p class="text-yellow-600">{{ $t('partners_profit_total') }}: {{ locale_float(row.fees_processing_result?.partners_profit_usdt_total) }} USDT</p>

					<p>{{ $t('topup_fixed_fee') }} (TRC20): {{ locale_float(row.fees_processing_result?.provider_trc20_topup_fixed_fee_amount_usdt) }} USDT</p>
				</div>
				<div v-else>{{ $t('wait_bank_confirmation') }}</div>
			</template>
		</ResponsiveTable>
		<div class="flex justify-end mt-4">
			<UPagination
				v-model="page"
				:page-count="per_page"
				:total="topupOrders?.meta?.filter_items_count || 0"
			/>
		</div>
	</div>
</template>
